/**
 * Geometry helpers for the VenueMap component. Fetches topojson from the
 * `@reuters-graphics/graphics-atlas-client` package via the jsdelivr CDN,
 * resolves a d3-geo projection, and returns a memoised `{ pathD, projection }`.
 */

import {
  geoPath,
  geoBounds,
  geoCentroid,
  type GeoProjection,
  type GeoConicProjection,
} from 'd3-geo';
import { feature } from 'topojson-client';
import type { Topology, Objects, GeometryObject } from 'topojson-specification';
import type {
  GeoJsonProperties,
  Feature,
  FeatureCollection,
  Geometry,
} from 'geojson';
import topoLookup from './topoLookup.json';

/** Each entry in `topoLookup.json`. */
export type TopoLookupEntry = {
  filename: string;
  /** Name of a d3-geo projection factory, e.g. "geoMercator". */
  projection?: string;
};

export type TopoLookup = Record<string, TopoLookupEntry>;

/** Memoised geometry returned by {@link loadGeometry} / {@link loadCombinedGeometry}. */
export type VenueGeometry = {
  pathD: string;
  projection: GeoProjection;
};

export const COMBINED_CACHE_KEY = '__combined__';
export const DEFAULT_PROJECTION = 'geoMercator';

/** Pinned atlas-client version. Bump to pull newer atlas data. */
export const ATLAS_CLIENT_VERSION = '0.5.2';
export const ATLAS_CDN_BASE = `https://cdn.jsdelivr.net/npm/@reuters-graphics/graphics-atlas-client@${ATLAS_CLIENT_VERSION}/topojson`;

export function buildTopoUrl(filename: string): string {
  return `${ATLAS_CDN_BASE}/${filename}`;
}

// geoAlbersUsa is composite — don't re-center it.
const NON_CENTERABLE_PROJECTIONS = new Set(['geoAlbersUsa']);

// Conic projections accept `.parallels()`.
const CONIC_PROJECTIONS = new Set([
  'geoAlbers',
  'geoConicConformal',
  'geoConicEqualArea',
  'geoConicEquidistant',
]);

// Cache of in-flight / resolved topojson fetches, keyed by filename.
const topoPromiseCache = new Map<
  string,
  Promise<Topology<Objects<GeoJsonProperties>> | null>
>();

function loadTopology(
  filename: string
): Promise<Topology<Objects<GeoJsonProperties>> | null> {
  const cached = topoPromiseCache.get(filename);
  if (cached) return cached;

  const url = buildTopoUrl(filename);
  const promise = fetch(url)
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status} ${response.statusText} for ${url}`
        );
      }
      return (await response.json()) as Topology<Objects<GeoJsonProperties>>;
    })
    .catch((err) => {
      console.error(
        `[VenueMap] Failed to fetch topojson "${filename}" from ${url}:`,
        err
      );
      // Evict so a later retry can try again.
      topoPromiseCache.delete(filename);
      return null;
    });

  topoPromiseCache.set(filename, promise);
  return promise;
}

// Lazy d3-geo import + projection factory cache.
let d3GeoModulePromise: Promise<typeof import('d3-geo')> | null = null;
function importD3Geo() {
  if (!d3GeoModulePromise) d3GeoModulePromise = import('d3-geo');
  return d3GeoModulePromise;
}

const projectionFactoryCache = new Map<string, () => GeoProjection>();

/** Resolve a d3-geo projection by name; falls back to `geoMercator`. */
export async function resolveProjection(
  name: string | undefined
): Promise<GeoProjection> {
  const projectionName = name || DEFAULT_PROJECTION;
  const cached = projectionFactoryCache.get(projectionName);
  if (cached) return cached();

  const d3geo = (await importD3Geo()) as unknown as Record<string, unknown>;
  const factory = d3geo[projectionName];

  if (typeof factory !== 'function') {
    if (projectionName !== DEFAULT_PROJECTION) {
      console.warn(
        `[VenueMap] Unknown d3-geo projection "${projectionName}", falling back to ${DEFAULT_PROJECTION}.`
      );
      return resolveProjection(DEFAULT_PROJECTION);
    }
    throw new Error(
      `[VenueMap] Could not resolve default projection "${DEFAULT_PROJECTION}" from d3-geo.`
    );
  }

  const typedFactory = factory as () => GeoProjection;
  projectionFactoryCache.set(projectionName, typedFactory);
  return typedFactory();
}

/**
 * Re-center a projection on the geometry's centroid. For conic projections,
 * also sets parallels at ~1/6 and 5/6 of the lat range (Snyder's convention).
 * Composite projections (geoAlbersUsa) are skipped.
 */
export function centerProjectionOnGeometry(
  projection: GeoProjection,
  projectionName: string,
  geojson: Feature | FeatureCollection
): GeoProjection {
  if (NON_CENTERABLE_PROJECTIONS.has(projectionName)) return projection;

  const [centerLon, centerLat] = geoCentroid(
    geojson as unknown as Parameters<typeof geoCentroid>[0]
  );

  if (!Number.isFinite(centerLon) || !Number.isFinite(centerLat)) {
    return projection;
  }

  projection.rotate([-centerLon, 0]).center([0, centerLat]);

  if (CONIC_PROJECTIONS.has(projectionName)) {
    const bounds = geoBounds(
      geojson as unknown as Parameters<typeof geoBounds>[0]
    );
    const [[, south], [, north]] = bounds;
    if (Number.isFinite(south) && Number.isFinite(north) && north > south) {
      const span = north - south;
      const parallels: [number, number] = [south + span / 6, north - span / 6];
      (projection as GeoConicProjection).parallels(parallels);
    }
  }

  return projection;
}

export function getTopoEntry(
  country: string | undefined
): TopoLookupEntry | undefined {
  if (!country) return undefined;
  const lookup = topoLookup as TopoLookup;
  return lookup[country];
}

async function topoToFeature(
  filename: string
): Promise<Feature<Geometry, GeoJsonProperties> | null> {
  try {
    const topology = await loadTopology(filename);
    if (!topology) return null;

    if (!topology?.objects || Object.keys(topology.objects).length === 0) {
      console.error(
        `[VenueMap] Topology loaded from "${filename}" is missing or has empty "objects".`
      );
      return null;
    }

    const geojson = feature(
      topology,
      Object.values(topology.objects)[0] as GeometryObject
    );
    return geojson as unknown as Feature<Geometry, GeoJsonProperties>;
  } catch (err) {
    console.error(`[VenueMap] Failed to parse topojson "${filename}":`, err);
    return null;
  }
}

// Memoised `{ pathD, projection }` cache.
const geometryCache = new Map<string, VenueGeometry>();

/** Load a single-country topology and produce a fitted, centered projection. */
export async function loadGeometry(
  entry: TopoLookupEntry,
  width: number,
  height: number
): Promise<VenueGeometry | null> {
  const { filename, projection: projectionName } = entry;
  const cacheKey = `${filename}::${projectionName ?? DEFAULT_PROJECTION}::${width}x${height}`;
  const cached = geometryCache.get(cacheKey);
  if (cached) return cached;

  const [geojson, baseProjection] = await Promise.all([
    topoToFeature(filename),
    resolveProjection(projectionName),
  ]);
  if (!geojson) return null;

  const centeredProjection = centerProjectionOnGeometry(
    baseProjection,
    projectionName ?? DEFAULT_PROJECTION,
    geojson as unknown as Feature
  );
  const projection = centeredProjection.fitSize(
    [width, height],
    geojson as unknown as Feature
  );
  const pathD =
    geoPath().projection(projection)(geojson as unknown as Feature) ?? '';

  if (!pathD) {
    console.warn(
      `[VenueMap] geoPath produced an empty path for "${filename}".`
    );
  }

  const result: VenueGeometry = { pathD, projection };
  geometryCache.set(cacheKey, result);
  return result;
}

/**
 * Merge every topojson in `topoLookup.json` into one basemap using
 * `geoMercator`. Per-country projections are ignored in combined mode.
 */
export async function loadCombinedGeometry(
  width: number,
  height: number
): Promise<VenueGeometry | null> {
  const cacheKey = `${COMBINED_CACHE_KEY}::${width}x${height}`;
  const cached = geometryCache.get(cacheKey);
  if (cached) return cached;

  const lookup = topoLookup as TopoLookup;
  const filenames = Array.from(
    new Set(Object.values(lookup).map((entry) => entry.filename))
  );

  const features: Feature<Geometry, GeoJsonProperties>[] = [];
  for (const filename of filenames) {
    const geojson = await topoToFeature(filename);
    if (!geojson) continue;
    const asCollection = geojson as unknown as FeatureCollection;
    if (asCollection.type === 'FeatureCollection') {
      features.push(...asCollection.features);
    } else {
      features.push(geojson);
    }
  }

  if (features.length === 0) {
    console.error('[VenueMap] No features available for combined basemap.');
    return null;
  }

  const collection: FeatureCollection = {
    type: 'FeatureCollection',
    features,
  };

  const baseProjection = await resolveProjection(DEFAULT_PROJECTION);
  const centeredProjection = centerProjectionOnGeometry(
    baseProjection,
    DEFAULT_PROJECTION,
    collection
  );
  const projection = centeredProjection.fitSize([width, height], collection);
  const pathD = geoPath().projection(projection)(collection) ?? '';

  const result: VenueGeometry = { pathD, projection };
  geometryCache.set(cacheKey, result);
  return result;
}
